/**
 * Offline Context
 * Provides offline status and sync functionality throughout the app
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  useOnlineStatus,
  useSyncQueue,
  registerServiceWorker,
  PersistentStorage,
} from '@/utils/offline';
import { PerformanceMonitor } from '@/utils/performance';

interface OfflineContextValue {
  isOnline: boolean;
  queueSize: number;
  storage: PersistentStorage;
  enqueueOperation: (type: string, data: any) => string;
  processQueue: (processor: (operation: any) => Promise<boolean>) => Promise<void>;
  clearQueue: () => void;
}

const OfflineContext = createContext<OfflineContextValue | null>(null);

export function useOffline(): OfflineContextValue {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within OfflineProvider');
  }
  return context;
}

interface OfflineProviderProps {
  children: ReactNode;
}

export function OfflineProvider({ children }: OfflineProviderProps) {
  const isOnline = useOnlineStatus();
  const { enqueue, processQueue, clearQueue, queueSize } = useSyncQueue();
  const [storage] = useState(() => new PersistentStorage('app'));

  // Register service worker on mount (skip in Tauri environment)
  useEffect(() => {
    // Check if running in Tauri
    const isTauri = typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window;
    
    // Only register ServiceWorker in browser environment, not in Tauri
    if (import.meta.env.PROD && !isTauri) {
      registerServiceWorker()
        .then((registration) => {
          if (registration) {
            console.log('[Offline] Service worker registered successfully');
          }
        })
        .catch((error) => {
          console.error('[Offline] Service worker registration failed:', error);
        });
    }
  }, []);

  // Initialize performance monitoring
  useEffect(() => {
    if (import.meta.env.DEV) {
      const monitor = PerformanceMonitor.getInstance();
      
      // Log performance report after 5 seconds
      const timer = setTimeout(() => {
        monitor.logReport();
      }, 5000);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, []);

  // Show notification when going offline/online
  useEffect(() => {
    if (!isOnline) {
      console.log('[Offline] You are offline. Changes will be synced when connection is restored.');
    } else if (queueSize > 0) {
      console.log(`[Offline] Back online! ${queueSize} pending operations will be synced.`);
    }
  }, [isOnline, queueSize]);

  const value: OfflineContextValue = {
    isOnline,
    queueSize,
    storage,
    enqueueOperation: enqueue,
    processQueue,
    clearQueue,
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
}
